import React, { useState, useEffect, useRef } from 'react';
import { Heart, ShoppingCart, ChevronDown, Search, Menu, X, Truck } from 'lucide-react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../context/CartContext';
import { CartPopup } from './CartPopup';
import { fetchProducts } from '../services/products';

interface HeaderProps {
  isScrolled: boolean;
  showMegaMenu: boolean;
  setShowMegaMenu: (show: boolean) => void;
  parts: string[];
}

interface Product {
  id: string;
  name: string;
  price: number;
  stock: number;
  images: string[];
  variants: Array<{
    id: string;
    name: string;
    price: number;
    discounted_price?: number;
    stock: number;
    images: string[];
    color: string;
  }>;
}

// Shared "Jupid" mega-menu items — used by both the desktop mega-menu and the
// mobile menu, and preloaded so the images render instantly on both.
const MEGA_MENU_PARTS = [
  { path: '/toukeratas/tald', label: 'Tallad', img: 'https://res.cloudinary.com/daka2lob2/image/upload/v1751278478/Deck2_xwo0ir.png', title: 'Tõukeratta tallad' },
  { path: '/toukeratas/lenks', label: 'Lenksud', img: 'https://res.cloudinary.com/daka2lob2/image/upload/v1751278479/Bar2_iyp6fz.png', title: 'Tõukeratta lenksud' },
  { path: '/toukeratas/rattad', label: 'Rattad', img: 'https://res.cloudinary.com/daka2lob2/image/upload/v1751278306/Wheel-removebg_pj6fon.png', title: 'Tõukeratta rattad' },
  { path: '/toukeratas/kahvlid', label: 'Kahvlid', img: 'https://res.cloudinary.com/daka2lob2/image/upload/v1751278382/Fork-removebg_z3xi3s.png', title: 'Tõukeratta kahvlid' },
  { path: '/toukeratas/klambrid', label: 'Klambrid', img: 'https://res.cloudinary.com/daka2lob2/image/upload/v1751278307/Clamp-removebg2_l4x35z.png', title: 'Tõukeratta klambrid' },
  { path: '/toukeratas/lenksuotsad', label: 'Lenksuotsad', img: 'https://res.cloudinary.com/daka2lob2/image/upload/v1774468541/Trick-scooter_Bar-ends_trikiratas_Lenksuotsad_j0zdrz.png', title: 'Tõukeratta lenksuotsad' },
  { path: '/toukeratas/peegid', label: 'Peegid', img: 'https://res.cloudinary.com/daka2lob2/image/upload/v1774468934/Trick-scooter_pegs_trikiratas_peegid_gvvx4u.png', title: 'Tõukeratta peegid' },
  { path: '/toukeratas/kaepidemed', label: 'Käepidemed', img: 'https://res.cloudinary.com/daka2lob2/image/upload/v1774469098/Trick-scooter_hand-grips_trikiratas_kaepidemed_qlorki.png', title: 'Tõukeratta käepidemed' },
  { path: '/toukeratas/gripteibid', label: 'Gripteibid', img: 'https://res.cloudinary.com/daka2lob2/image/upload/v1774469325/Trick-scooter_griptape_trikiratas_gripteip_meunin.png', title: 'Tõukeratta gripteibid' },
];

export function Header({ isScrolled, showMegaMenu, setShowMegaMenu, parts }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { items } = useCart();
  const isHomePage = location.pathname === '/';
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const [showPartsMenu, setShowPartsMenu] = useState(false);
  const partsMenuTimeout = useRef<NodeJS.Timeout>();
  const [showCartPopup, setShowCartPopup] = useState(false);
  const cartTimeoutRef = useRef<NodeJS.Timeout>();
  const headerRef = useRef<HTMLElement>(null);
  const [headerBottom, setHeaderBottom] = useState(87);

  // Measure the real rendered header height so the mobile menu always
  // starts exactly where the header visually ends (never overlaps it).
  useEffect(() => {
    const measure = () => {
      if (headerRef.current) {
        setHeaderBottom(headerRef.current.getBoundingClientRect().bottom);
      }
    };
    measure();
    const timeout = setTimeout(measure, 300); // after fonts/images settle
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      clearTimeout(timeout);
    };
  }, [isScrolled, isHomePage]);

  const handlePartsMouseEnter = () => {
    if (partsMenuTimeout.current) {
      clearTimeout(partsMenuTimeout.current);
    }
    setShowPartsMenu(true);
  };

  const handlePartsMouseLeave = () => {
    partsMenuTimeout.current = setTimeout(() => {
      setShowPartsMenu(false);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (partsMenuTimeout.current) {
        clearTimeout(partsMenuTimeout.current);
      }
    };
  }, []);

  const handleCartMouseEnter = () => {
    if (cartTimeoutRef.current) {
      clearTimeout(cartTimeoutRef.current);
    }
    setShowCartPopup(true);
  };

  const handleCartMouseLeave = () => {
    cartTimeoutRef.current = setTimeout(() => {
      setShowCartPopup(false);
    }, 300);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      try {
        // Fetch all products from Supabase
        const allProducts = await fetchProducts();
        
        // Filter products based on search query
        const filtered = allProducts
          .filter((product: Product) => {
            if (!product.variants || !Array.isArray(product.variants)) return false;
            return product.variants.some(variant => 
              variant && variant.name && variant.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
          })
          .slice(0, 5);
          
        console.log("Search results:", filtered);
        setSearchResults(filtered);
      } catch (error) {
        console.error('Error fetching search results:', error);
      }
    };

    const debounceTimer = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const scrollToContact = () => {
    if (isHomePage) {
      const contactSection = document.querySelector('#contact-section');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollToContact: true } });
    }
  };

  useEffect(() => {
    if (location.state?.scrollToContact) {
      const contactSection = document.querySelector('#contact-section');
      if (contactSection) {
        setTimeout(() => {
          contactSection.scrollIntoView({ behavior: 'smooth' });
          window.history.replaceState({}, document.title);
        }, 100);
      }
    }
  }, [location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/toukeratas?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchResults(false);
    }
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Peamine navigatsioon",
          "itemListElement": [
            { "@type": "SiteNavigationElement", "position": 1, "name": "Trikitõuksid", "url": "https://xn--tuks-0qa.ee/toukeratas" },
            { "@type": "SiteNavigationElement", "position": 2, "name": "Tallad", "url": "https://xn--tuks-0qa.ee/toukeratas/tald" },
            { "@type": "SiteNavigationElement", "position": 3, "name": "Lenksud", "url": "https://xn--tuks-0qa.ee/toukeratas/lenks" },
            { "@type": "SiteNavigationElement", "position": 4, "name": "Brändid", "url": "https://xn--tuks-0qa.ee/toukeratas/brandid" }
          ]
        })}</script>
        {/* Prerender/preload the "Jupid" mega-menu images (desktop + mobile) */}
        {MEGA_MENU_PARTS.map((part) => (
          <link key={part.img} rel="preload" as="image" href={part.img} />
        ))}
      </Helmet>
      {/* Real top announcement bar — sits above the header */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-8 bg-[#4FC1E9] text-white flex items-center justify-center">
        <div className="container mx-auto px-3 sm:px-6 flex items-center justify-center gap-2 text-[13px] sm:text-sm font-semibold">
          <Truck className="w-4 h-4 text-white flex-shrink-0" />
          <span className="whitespace-nowrap">Tasuta kulleriga koju kõik tellimused üle Eesti!</span>
        </div>
      </div>

      <header
        ref={headerRef}
        className={`fixed w-full top-8 z-50 transition-all duration-500 ${
          isHomePage
            ? isScrolled
              ? 'bg-black/80 backdrop-blur-sm'
              : 'bg-transparent'
            : 'bg-black/80 backdrop-blur-sm'
        } py-6 lg:py-4`}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <button
              className="lg:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Sulge menüü" : "Ava menüü"}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            <nav className="hidden lg:flex items-center space-x-8">
              <Link
                to="/toukeratas"
                className="text-white hover:text-[#4FC1E9] transition-all duration-300 font-medium"
              >
                Trikitõuksid
              </Link>
              <div
                className="relative group"
                onMouseEnter={handlePartsMouseEnter}
                onMouseLeave={handlePartsMouseLeave}
              >
                <button
                  onClick={() => navigate('/toukeratas/toukeratta-varuosad')}
                  className="text-white hover:text-[#4FC1E9] transition-all duration-300 font-medium flex items-center"
                >
                  Jupid
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>
                {showPartsMenu && (
                  <div
                    className="absolute top-[100%] left-1/2 -translate-x-1/2 w-[540px] bg-black/80 backdrop-blur-sm py-4 shadow-xl border border-white/10 rounded-xl z-50"
                    onMouseEnter={handlePartsMouseEnter}
                    onMouseLeave={handlePartsMouseLeave}
                  >
                    <div className="grid grid-cols-3 gap-3 px-4">
                      {MEGA_MENU_PARTS.map(({ path, label, img, title }) => (
                        <button
                          key={path}
                          onClick={() => { navigate(path); setShowPartsMenu(false); }}
                          className="block bg-[#4FC1E9]/10 rounded-lg p-2 hover:scale-105 transition-transform"
                        >
                          <img src={img} alt={label} title={title} loading="eager" decoding="async" fetchpriority="high" className="w-full h-16 object-contain rounded mb-1" />
                          <span className="block text-center font-medium text-white text-sm">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Lastele nav link - commented out, using category banner instead
              <Link
                to="/toukeratas/laste-toukeratas"
                className="text-white hover:text-[#4FC1E9] transition-all duration-300 font-medium"
              >
                Lastele
              </Link>
              */}
              <Link
                to="/toukeratas/brandid"
                className="text-white hover:text-[#4FC1E9] transition-all duration-300 font-medium"
              >
                Brändid
              </Link>
              {/* Soodus link - commented out temporarily
              <Link
                to="/toukeratas/soodus-tooted"
                className="text-white hover:text-[#4FC1E9] transition-all duration-300 font-medium"
              >
                Soodus
              </Link>
              */}
              <Link
                to="/kontakt"
                className="text-white hover:text-[#4FC1E9] transition-all duration-300 font-medium"
              >
                Kontakt
              </Link>
              {/*<Link 
                to="/ehita-touks" 
                className="text-white hover:text-[#4FC1E9] transition-all duration-300 font-medium"
              >
                Ehita tõuks
              </Link>*/}
            </nav>

            <Link to="/" className="absolute left-1/2 transform -translate-x-1/2">
              <img
                src="https://res.cloudinary.com/daka2lob2/image/upload/v1751272742/Touks_logo3_ed9w6g.png"
                alt="Tõuks"
                title="Tõuks - Trikitõuksid ja varuosad"
                className="h-12"
              />
            </Link>

            <nav className="flex items-center space-x-6">
              <div className="relative hidden lg:block" ref={searchRef}>
                <form onSubmit={handleSearchSubmit}>
                  <input
                    type="text"
                    placeholder="Otsi..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSearchResults(true);
                    }}
                    onFocus={() => setShowSearchResults(true)}
                   className="bg-white/10 text-white placeholder-white/60 border border-white/20 rounded-full px-4 py-3 focus:outline-none focus:border-[#4FC1E9] w-80"
                  />
                  <button 
                    type="submit" 
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 p-3"
                    aria-label="Otsi"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                </form>

                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg overflow-hidden z-50">
                    {searchResults.map((product) => {
                      // Find the first matching variant to display
                      const matchingVariant = product.variants?.find(v => 
                        v && v.name && v.name.toLowerCase().includes(searchQuery.toLowerCase())
                      );
                      
                      if (!matchingVariant) return null;
                      
                      return (
                        <Link
                          key={matchingVariant.id}
                          to={`/tooted/${matchingVariant.id}`}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors"
                          onClick={() => setShowSearchResults(false)}
                        >
                          <img
                            src={matchingVariant.images && matchingVariant.images.length > 0
                              ? matchingVariant.images[0]
                              : 'https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg'
                            }
                            alt={matchingVariant.name}
                            title={matchingVariant.name}
                            className="w-12 h-12 object-cover rounded"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              if (!target.src.includes('https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg')) {
                                target.src = 'https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg';
                              }
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {matchingVariant.name}
                            </p>
                            <p className="text-sm text-[#4FC1E9]">
                              {typeof matchingVariant.discounted_price === 'number'
                                ? matchingVariant.discounted_price.toFixed(2)
                                : typeof matchingVariant.price === 'number'
                                  ? matchingVariant.price.toFixed(2)
                                  : '0.00'
                              }€
                            </p>
                            <p className="text-xs text-gray-500">
                              {typeof matchingVariant.stock === 'number' && matchingVariant.stock > 0 ? 'Laos' : 'Otsas'}
                            </p>
                          </div>
                        </Link>
                      );
                    })}
                    
                    <div className="p-3 border-t">
                      <button
                        onClick={() => {
                          navigate(`/toukeratas?search=${encodeURIComponent(searchQuery)}`);
                          setShowSearchResults(false);
                        }}
                        className="text-[#4FC1E9] hover:text-[#3ba8d0] text-sm w-full text-center"
                      >
                        Vaata kõiki tulemusi
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <Link 
                to="/ostukorv" 
                className="text-white hover:text-[#4FC1E9] transition-all duration-300 transform hover:scale-110"
                aria-label="Lemmikud"
              >
                <Heart className="w-5 h-5" />
              </Link>
              <div 
                className="relative"
                onMouseEnter={handleCartMouseEnter}
                onMouseLeave={handleCartMouseLeave}
              >
              <Link
                to="/ostukorv"
                className="text-white hover:text-[#4FC1E9] transition-all duration-300 transform hover:scale-110"
                aria-label={`Ostukorv ${items.length > 0 ? `(${items.length} toodet)` : '(tühi)'}`}
              >
                <ShoppingCart className="w-5 h-5"/>
              </Link>
              {items.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#4FC1E9] text-white text-xs w-4 h-4 rounded-full flex items-center justify-center pointer-events-none">
                  {items.length}
                </span>
              )} 
              {showCartPopup && <CartPopup />}
              </div>
            </nav>
          </div>
        </div>

        {/* Mobile menu - anchored to the header itself so it always begins
            exactly where the header's own height ends (top-full = 100% of the
            fixed header = its bottom edge), no measurement/timing gap. */}
        <div
          className={`
            lg:hidden absolute top-full inset-x-0 bg-black/80 backdrop-blur-sm
            transition-all duration-300 ease-in-out border-t border-white/10
            ${mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}
          `}
        >
          <div className="container mx-auto px-6 py-4 max-h-[80vh] overflow-y-auto">
            <nav className="flex flex-col space-y-4">
              {/* Search — first thing in the mobile menu, above "Trikitõuksid" */}
              <form className="relative" onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  placeholder="Otsi..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  className="w-full bg-white/10 text-white placeholder-white/60 border border-white/20 rounded-full px-4 py-2 focus:outline-none focus:border-[#4FC1E9]"
                />
                <button type="submit" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60">
                  <Search className="w-4 h-4" />
                </button>
              </form>

              <Link to="/toukeratas" className="text-white hover:text-[#4FC1E9] transition-all duration-300 font-medium">
                Trikitõuksid
              </Link>

              {/* Jupid — image + text grid, 3 columns × 3 rows (same as desktop) */}
              <Link to="/toukeratas/toukeratta-varuosad" className="text-white hover:text-[#4FC1E9] transition-all duration-300 font-medium">
                Jupid
              </Link>
              <div className="grid grid-cols-3 gap-3">
                {MEGA_MENU_PARTS.map(({ path, label, img, title }) => (
                  <Link
                    key={path}
                    to={path}
                    className="block bg-[#4FC1E9]/10 rounded-lg p-2 hover:scale-105 transition-transform"
                  >
                    <img src={img} alt={label} title={title} loading="eager" decoding="async" fetchpriority="high" className="w-full h-16 object-contain rounded mb-1" />
                    <span className="block text-center font-medium text-white text-sm">{label}</span>
                  </Link>
                ))}
              </div>

              <Link to="/kontakt" className="text-white hover:text-[#4FC1E9] transition-all duration-300 font-medium">
                Kontakt
              </Link>
            </nav>
          </div>
        </div>

        <div className="header-separator"></div>
      </header>
      <div style={{ height: isHomePage ? 32 : headerBottom }}></div>
    </>
  );
}
